import dayjs from 'dayjs'
import {
  IncidentReportWithCase,
  IncidentReportUpdateCaseBody,
  IncidentReportCreateCaseBody,
  IncidentReportItem
} from '@lmc/ts-common'
import { incidentReportsConstants } from 'locale/incidentReports'
import { getCombinedTimeIso } from '../utils'
import {
  IncidentReportEditCaseForm,
  IncidentReportCreateCaseForm
} from '../components/forms/IncidentCaseForm'
import { AttachmentFieldType } from '../components/UploadAttachmentsOnSaveInput'

const coerceNoDetailsText = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string' && value.trim() === '') return null
  return value as string
}

const coerceNoDetails = (values: any) => {
  if (values.restrictivePracticesStatus === 'yes') {
    return {
      ...values,
      noRestraintsDetails: null
    }
  }

  if (values.injuriesStatus === 'yes') {
    return {
      ...values,
      noInjuriesDetails: null
    }
  }

  if (values.damagesStatus === 'yes') {
    return {
      ...values,
      noDamagesDetails: null
    }
  }

  return values
}

const coerceDetailsObject = (value: any, forceOff: boolean = false): any => {
  // If we want to force "off", let's drop
  // everything and do it immediately.
  if (forceOff) {
    return { hasBeenApplied: false }
  }

  // If an update came in with undefined / null
  // values, let's set to false
  if (!value) {
    return { hasBeenApplied: false }
  }

  // If it's set to false, let's make sure we drop
  // the other fields too
  if (!value.hasBeenApplied) {
    return { hasBeenApplied: false }
  }

  return {
    ...value,
    hasBeenApplied: true
  }
}

// Before we submit, we go in and make sure all
// fields are in the expected format of the API,
// if we've left any fields not filled out, make
// sure they are in the appropriate default state
// for "off" etc.
const coerceIncidentCaseValues = <T extends { [key: string]: any }>(
  values: T
): T => {
  const restrictiveOff = values.restrictivePracticesStatus === 'no'
  const injuriesOff = values.injuriesStatus === 'no'
  const damagesOff = values.damagesStatus === 'no'

  return {
    ...values,
    physicalIntervention: coerceDetailsObject(
      values.physicalIntervention,
      restrictiveOff
    ),
    restraints: coerceDetailsObject(values.restraints, restrictiveOff),
    chemicalRestraints: coerceDetailsObject(
      values.chemicalRestraints,
      restrictiveOff
    ),
    environmentalRestraints: coerceDetailsObject(
      values.environmentalRestraints,
      restrictiveOff
    ),
    verbalRestraints: coerceDetailsObject(
      values.verbalRestraints,
      restrictiveOff
    ),
    residentInjuries: coerceDetailsObject(values.residentInjuries, injuriesOff),
    carersInjuries: coerceDetailsObject(values.carersInjuries, injuriesOff),
    otherPartiesInjuries: coerceDetailsObject(
      values.otherPartiesInjuries,
      injuriesOff
    ),
    damages: coerceDetailsObject(values.damages, damagesOff),
    damagesIntroducingRisk: coerceDetailsObject(
      values.damagesIntroducingRisk,
      damagesOff
    ),
    // Medications are not tied to a section-level radio, but should still clear
    // details when "hasBeenApplied" is false.
    prnMedication: coerceDetailsObject(values.prnMedication, false)
  } as T
}

export const prepareUpdateInitialValues = (
  incidentReport: IncidentReportWithCase,
  t: (key: string) => string
): Partial<IncidentReportEditCaseForm<string>> => {
  return {
    ...incidentReport.case,
    attachments: incidentReport.attachments.map((attachmentUrl, index) => ({
      name: `${t(incidentReportsConstants.FILE)} ${index + 1}`,
      isDeleted: false,
      isNew: false,
      url: attachmentUrl
    })),
    serviceUsersInvolved: incidentReport.case.serviceUsersInvolved.map(
      (serviceUser) => serviceUser.id
    ),
    carersInvolved: incidentReport.case.carersInvolved?.map(
      (serviceUser) => serviceUser.id
    ),
    severity: incidentReport.severity,
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id
    }
  }
}

export const prepareUpdateBody = (
  values: IncidentReportEditCaseForm<string>
): IncidentReportUpdateCaseBody => {
  const normalized = coerceNoDetails(coerceIncidentCaseValues(values))

  return {
    severity: normalized.severity,
    serviceUsersInvolved: normalized.serviceUsersInvolved,
    otherPeopleInvolved: normalized.otherPeopleInvolved,
    otherWitnesses: normalized.otherWitnesses,
    carersInvolved: normalized.carersInvolved,
    physicalIntervention: normalized.physicalIntervention,
    restraints: normalized.restraints,
    chemicalRestraints: normalized.chemicalRestraints,
    environmentalRestraints: normalized.environmentalRestraints,
    verbalRestraints: normalized.verbalRestraints,
    residentInjuries: normalized.residentInjuries,
    carersInjuries: normalized.carersInjuries,
    otherPartiesInjuries: normalized.otherPartiesInjuries,
    damages: normalized.damages,
    damagesIntroducingRisk: normalized.damagesIntroducingRisk,
    prnMedication: normalized.prnMedication,
    noRestraintsDetails: coerceNoDetailsText(normalized.noRestraintsDetails),
    noInjuriesDetails: coerceNoDetailsText(normalized.noInjuriesDetails),
    noDamagesDetails: coerceNoDetailsText(normalized.noDamagesDetails),
    serviceUserOpinion: normalized.serviceUserOpinion,
    incidentOtherInformation: normalized.incidentOtherInformation,
    debriefingNotes: normalized.debriefingNotes,
    rootCause: normalized.rootCause,
    preventativeActions: normalized.preventativeActions,
    otherNotes: normalized.otherNotes,
    log: {
      ...normalized.log,
      timeLogged: getCombinedTimeIso(
        normalized?.log?.dateLogged,
        normalized?.log?.timeLogged
      )
    },
    followUpActions: {
      ...normalized.followUpActions,
      actions: normalized?.followUpActions?.actions?.map(
        (action: {
          id?: string
          title: string
          status: string
          assignedUserId?: string | null
        }) => ({
          id: action.id,
          title: action.title,
          status: action.status,
          assignedUserId: action.assignedUserId ?? null
        })
      )
    }
  } as any as IncidentReportUpdateCaseBody
}

export const getDeletedAttachmentsIndexes = (
  attachments: AttachmentFieldType[]
): number[] => {
  return attachments
    .map((attachment, index) =>
      attachment.isDeleted === true ? index : undefined
    )
    .filter((index): index is number => index !== undefined)
}

export const prepareCreateInitialValues = (
  incidentReport: IncidentReportItem,
  t: (key: string) => string
): Partial<IncidentReportCreateCaseForm<string>> => {
  // If the incident already has a pre-populated draft case (e.g. created on
  // mobile), prefer those case values so that the create-case screen shows
  // the same data instead of resetting everything to defaults.
  const caseData = (incidentReport as any)?.case

  if (caseData) {
    return {
      ...caseData,
      // Normalise related-entity arrays into IDs, to match the edit case flow.
      serviceUsersInvolved:
        caseData.serviceUsersInvolved?.map(
          (serviceUser: { id: string }) => serviceUser.id
        ) ?? [],
      carersInvolved:
        caseData.carersInvolved?.map((carer: { id: string }) => carer.id) ?? [],
      // Severity is stored at the top level of the incident report.
      severity: incidentReport.severity ?? null,
      // Make sure follow-up actions always have a well-defined shape.
      followUpActions: caseData.followUpActions ?? {
        actions: [],
        notificationRequired: false,
        safeguardingActionsRequired: false
      },
      // Attachments live on the root incident report, same as in update flow.
      attachments:
        incidentReport.attachments?.map((attachmentUrl, index) => ({
          name: `${t(incidentReportsConstants.FILE)} ${index + 1}`,
          isDeleted: false,
          isNew: false,
          url: attachmentUrl
        })) ?? [],
      // Log fields are always derived from the root log object.
      log: {
        ...incidentReport.log,
        goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
        dateLogged: dayjs(incidentReport.log.timeLogged),
        timeLogged: dayjs(incidentReport.log.timeLogged),
        item: incidentReport.log.item?.id
      }
    }
  }

  return {
    ...incidentReport,
    severity: null,
    otherPeopleInvolved: null,
    physicalIntervention: { hasBeenApplied: false },
    restraints: { hasBeenApplied: false },
    chemicalRestraints: { hasBeenApplied: false },
    environmentalRestraints: { hasBeenApplied: false },
    verbalRestraints: { hasBeenApplied: false },
    residentInjuries: { hasBeenApplied: false },
    carersInjuries: { hasBeenApplied: false },
    otherPartiesInjuries: { hasBeenApplied: false },
    damages: { hasBeenApplied: false },
    damagesIntroducingRisk: { hasBeenApplied: false },
    prnMedication: { hasBeenApplied: false },
    noRestraintsDetails: null,
    noInjuriesDetails: null,
    noDamagesDetails: null,
    otherClientsInvolved: [],
    serviceUsersInvolved: [],
    attachments: [],
    followUpActions: {
      actions: [],
      notificationRequired: false,
      safeguardingActionsRequired: false
    },
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id
    }
  }
}

export const prepareCreateBody = (
  values: IncidentReportCreateCaseForm<string>
): IncidentReportCreateCaseBody => {
  const normalized = coerceNoDetails(coerceIncidentCaseValues(values))

  const {
    noRestraintsDetails,
    noInjuriesDetails,
    noDamagesDetails,
    noInjuriesOccurred,
    damagesStatus,
    injuriesStatus,
    restrictivePracticesStatus,
    ...rest
  } = normalized as any

  return {
    ...(rest as unknown as IncidentReportCreateCaseBody),
    // keep the "no*Details" fields in the payload, but drop UI-only flags
    noRestraintsDetails: coerceNoDetailsText(noRestraintsDetails),
    noInjuriesDetails: coerceNoDetailsText(noInjuriesDetails),
    noDamagesDetails: coerceNoDetailsText(noDamagesDetails),
    log: {
      ...normalized.log,
      timeLogged: getCombinedTimeIso(
        normalized?.log?.dateLogged,
        normalized?.log?.timeLogged
      )
    },
    followUpActions: {
      ...normalized.followUpActions,
      actions: normalized?.followUpActions?.actions?.map(
        (action: {
          title: string
          status: string
          assignedUserId?: string | null
        }) => ({
          title: action.title,
          status: action.status,
          assignedUserId: action.assignedUserId ?? null
        })
      )
    }
  } as any as IncidentReportCreateCaseBody
}
